import { createSlice, PayloadAction, Slice } from "@reduxjs/toolkit";
import {
  birthInfoReducer,
  fiscalCodeReducer,
  personalInfoReducer,
} from "./reducers/personal-info.reducer.ts";
import { Contacts } from "./types/contacts.ts";

import { BirthInfo } from "./types/birth-info.ts";
import { AddressInfo } from "./types/address.ts";
import { Living } from "./types/living.ts";
import { submitLivingSituationReducer } from "./reducers/submit-living-situation.reducer.ts";
import { submitHiringReducer } from "./reducers/submit-hiring.reducer.ts";
import { submitAddressReducer } from "./reducers/submit-address.reducer.ts";
import { Personal } from "./types/personal.ts";
import { JobInfo } from "./types/job.ts";
import { FamilyInfo } from "./types/family.ts";
import { submitBirthAction } from "../../actions/submit-birth.action.ts";
import { submitAddress } from "../../actions/submit-address.action.ts";
import { submitMaritalStatus } from "../../actions/submit-marital-status.action.ts";
import { submitMaritalStatusReducer } from "./reducers/submit-marital-status.reducer.ts";
import { submitFamily } from "../../actions/submit.family.action.ts";
import { submitContacts } from "../../actions/submit-contacts.action.ts";
import { submitContactsReducer } from "./reducers/submit-contacts.reducer.ts";
import { submitFamilyReducer } from "./reducers/submit-family.reducer..ts";
import { submitLivingSituation } from "../../actions/submit-living-situation.action.ts";
import { submitEmployment } from "../../actions/submit-employment.action.ts";
import { submitEmploymentReducer } from "./reducers/submit-employment.reducer.ts";
import { submitVatNumber } from "../../actions/submit-vat-number.action.ts";
import { submitVatNumberReducer } from "./reducers/submit-vat-number.reducer..ts";
import { submitFiscalCodeAction } from "../../actions/submit-fiscal-code.action.ts";
import { submitPersonalInfo } from "../../actions/submit-personal-info.action.ts";
import { submitHiring } from "../../actions/submit-hiring.action.ts";
import {
  CompanyIndustryRequest,
  CompanySizeRequest,
  GetOrchSurveyResponse,
  KycTentative,
} from "../../../orchestrator-api-client";
import { submitCompanyIndustry } from "../../actions/submit-company-industry.action.ts";
import { submitCompanySize } from "../../actions/submit-company-size.action.ts";
import { submitGrossIncome } from "../../actions/submit-gross-income.action.ts";
import { submitIncome } from "../../actions/submit-income.action.ts";
import { submitIncomeReducer } from "./reducers/submit-income.reducer.ts";
import { submitOtherIncome } from "../../actions/submit-other-income.ts";
import { submitOtherIncomeReducer } from "./reducers/submit-other-income.reducer.ts";
import { grossIncomeReducer } from "./reducers/submit-gross-income.reducer.ts";
import initStoreAction from "../../actions/init-store.action.ts";
import initStoreReducer from "./reducers/init-store.reducer.ts";
import { submitAdvData } from "../../actions/submit-adv-data.actions.ts";
import { submitAdvDataReducer } from "./reducers/submit-adv-data.reducer.ts";
import { getAdvQuestionnaires } from "../../actions/get-adv-questionnaires.action.ts";
import { getAdvDataReducer } from "./reducers/get-adv-data.reducer.ts";
import { getCoherenceQuestionnaireData } from "../../actions/get-coherence-questionnaire-data.action.ts";
import { coherenceQuestionnaireReducer } from "./reducers/coherence-questionnaire.reducer.ts";

export interface CustomerState {
  personalInfo: Personal;
  contacts: Contacts;
  birth: BirthInfo;
  addressInfo: AddressInfo;
  job: JobInfo;
  family: FamilyInfo;
  living: Living;
  advData: Record<string, string>;
  advQuestions: GetOrchSurveyResponse;
  cpiQuestionnaire: GetOrchSurveyResponse;
  kycTentative: KycTentative;
}

const defaultPersonalInfo = {
  firstName: "",
  lastName: "",
  gender: "",
  fiscalCode: "",
  nationality: "ITA",
};

const defaultContacts = {
  email: "",
  phonePrefix: "+39",
  phone: "",
};

const defaultBirth = {
  birthDate: {
    birthDay: undefined,
    birthMonth: undefined,
    birthYear: undefined,
  },
  birthCountry: "",
  birthProvince: "",
  birthCity: "",
};

const defaultJob: JobInfo = {
  educationLevel: "",
  employmentStatus: "",
  jobRole: "",
  hireMonth: undefined,
  hireYear: undefined,
  vatNumber: "",
  employerName: "",
  companyIndustry: "",
  companySize: "",
  income: {
    netMonthlySalary: undefined,
    monthsSalary: 0,
    grossAnnualSalary: undefined,
    hasOtherIncome: undefined,
    otherIncomeDescription: undefined,
    otherIncomeNetMonthlySalary: undefined,
  },
};

const defaultAddress: AddressInfo = {
  residence: {
    zipCode: "",
    streetName: "",
    streetNumber: "",
    city: "",
    province: "",
    country: "",
  },
};

const defaultFamily: FamilyInfo = {
  maritalStatus: "",
  householdMembers: undefined,
  peopleWithIncome: "",
};

const defaultLiving: Living = {
  homeStatus: "",
};

const defaultAdvData = {};

const advQuestionnaires: GetOrchSurveyResponse = {
  questions: [],
};

const cpiQuestionnaireDefaultData: GetOrchSurveyResponse = {
  questions: [],
};

const defaultKycTentativeData = {
  spid: 2,
  videoSelfie: 2,
};

const initialState: CustomerState = {
  personalInfo: defaultPersonalInfo,
  contacts: defaultContacts,
  birth: defaultBirth,
  job: defaultJob,
  addressInfo: defaultAddress,
  family: defaultFamily,
  living: defaultLiving,
  advData: defaultAdvData,
  advQuestions: advQuestionnaires,
  cpiQuestionnaire: cpiQuestionnaireDefaultData,
  kycTentative: defaultKycTentativeData,
};

const customerSlice: Slice<CustomerState> = createSlice({
  name: "customer",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(initStoreAction.fulfilled, initStoreReducer);
    builder.addCase(submitContacts.fulfilled, submitContactsReducer);
    builder.addCase(submitPersonalInfo.fulfilled, personalInfoReducer);
    builder.addCase(submitBirthAction.fulfilled, birthInfoReducer);
    builder.addCase(submitFiscalCodeAction.fulfilled, fiscalCodeReducer);
    builder.addCase(submitEmployment.fulfilled, submitEmploymentReducer);
    builder.addCase(submitIncome.fulfilled, submitIncomeReducer);
    builder.addCase(submitOtherIncome.fulfilled, submitOtherIncomeReducer);
    builder.addCase(submitVatNumber.fulfilled, submitVatNumberReducer);
    builder.addCase(submitAddress.fulfilled, submitAddressReducer);
    builder.addCase(submitMaritalStatus.fulfilled, submitMaritalStatusReducer);
    builder.addCase(submitFamily.fulfilled, submitFamilyReducer);
    builder.addCase(submitGrossIncome.fulfilled, grossIncomeReducer);
    builder.addCase(
      submitLivingSituation.fulfilled,
      submitLivingSituationReducer,
    );
    builder.addCase(submitHiring.fulfilled, submitHiringReducer);
    builder.addCase(
      submitCompanyIndustry.fulfilled,
      (state: CustomerState, action: PayloadAction<CompanyIndustryRequest>) => {
        state.job.companyIndustry = action.payload.companyIndustry!; // TODO: Must be set as non-nullable on swagger
      },
    );
    builder.addCase(
      submitCompanySize.fulfilled,
      (state: CustomerState, action: PayloadAction<CompanySizeRequest>) => {
        state.job.companySize = action.payload.companySize!; // TODO: Must be set as non-nullable on swagger
      },
    );
    builder.addCase(getAdvQuestionnaires.fulfilled, getAdvDataReducer);
    builder.addCase(submitAdvData.fulfilled, submitAdvDataReducer);
    builder.addCase(
      getCoherenceQuestionnaireData.fulfilled,
      coherenceQuestionnaireReducer,
    );
  },
});

const customerReducer = customerSlice.reducer;
export default customerReducer;
